import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Approval {
  id: string
  model: string
  requestor: string
  type: string
  risk_level: string
  status: string
  submitted: string
}

interface BiasAudit {
  model: string
  audit_date: string
  fairness_score: number
  protected_attrs: string[]
  bias_detected: boolean
  status: string
}

interface Statistics {
  models_governed: number
  compliance_rate: number
  pending_approvals: number
  bias_audits_mtd: number
  documentation_coverage: number
  risk_assessments: number
}

export default function ModelGovernancePage() {
  const [approvals, setApprovals] = useState<Approval[]>([])
  const [audits, setAudits] = useState<BiasAudit[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [approvalsRes, auditsRes, statsRes] = await Promise.all([
        fetch('/api/modelgovernance/approvals'),
        fetch('/api/modelgovernance/bias-audits'),
        fetch('/api/modelgovernance/statistics')
      ])
      if (approvalsRes.ok) setApprovals(await approvalsRes.json())
      if (auditsRes.ok) setAudits(await auditsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching model governance:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    if (status === 'Approved' || status === 'Passed') return 'bg-green-100 text-green-800'
    if (status === 'Pending' || status === 'Under Review') return 'bg-yellow-100 text-yellow-800'
    if (status === 'Remediation') return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  const getRiskBadge = (risk: string) => {
    if (risk === 'High') return 'bg-red-100 text-red-800'
    if (risk === 'Medium') return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Model Governance</h1>
          <p className="mt-2 text-gray-600">Phase 279 - ML model governance and compliance</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Models Governed</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.models_governed}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Compliance Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.compliance_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Pending Approvals</p>
              <p className="text-2xl font-bold text-yellow-600">{statistics.pending_approvals}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Bias Audits MTD</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.bias_audits_mtd}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Doc Coverage</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.documentation_coverage}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Risk Assessments</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.risk_assessments}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Bias Audits</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {audits.map((audit, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{audit.model}</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(audit.status)}`}>{audit.status}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">Fairness Score</span>
                  <span className={`font-bold ${audit.fairness_score >= 0.9 ? 'text-green-600' : audit.fairness_score >= 0.85 ? 'text-yellow-600' : 'text-red-600'}`}>{audit.fairness_score}</span>
                </div>
                <div className="text-sm text-gray-500">
                  Protected: {audit.protected_attrs.join(', ')}
                </div>
                <div className="text-xs text-gray-400 mt-1">{audit.audit_date}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Model Approvals</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requestor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Level</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {approvals.map((apr) => (
                  <tr key={apr.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{apr.model}</td>
                    <td className="px-6 py-4 text-purple-600">{apr.requestor}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{apr.type}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getRiskBadge(apr.risk_level)}`}>{apr.risk_level}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(apr.status)}`}>{apr.status}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{apr.submitted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
